/**
 * Form Validation Utilities for Busyplace Legal Forms
 * 
 * This module provides validation functions and French error messages
 * for legal document forms (SARL, SAS, SCI, etc.)
 * 
 * Usage:
 *   import { validateField, VALIDATION_MESSAGES } from '../_helpers/formValidation';
 *   
 *   const error = validateField('denomination', value, { required: true, minLength: 2 });
 *   // Returns: null if valid, or French error message string if invalid
 */

// ============================================
// FRENCH ERROR MESSAGES FOR LEGAL FORMS
// ============================================

export const VALIDATION_MESSAGES = {
  // Required fields
  required: 'Ce champ est obligatoire',
  requiredSelect: 'Veuillez sélectionner une option',
  requiredDate: 'Veuillez sélectionner une date',
  
  // Text validation
  minLength: (min) => `Ce champ doit contenir au moins ${min} caractères`,
  maxLength: (max) => `Ce champ ne doit pas dépasser ${max} caractères`,
  
  // Number validation
  positiveNumber: 'Le montant doit être supérieur à 0',
  minValue: (min) => `La valeur doit être supérieure ou égale à ${min}`,
  maxValue: (max) => `La valeur doit être inférieure ou égale à ${max}`,
  invalidNumber: 'Veuillez entrer un nombre valide',
  
  // Specific legal fields
  denomination: 'La dénomination sociale est obligatoire',
  capital: 'Le capital social doit être supérieur à 0',
  duree: 'La durée de la société doit être comprise entre 1 et 99 ans',
  siren: 'Le numéro SIREN doit contenir 9 chiffres',
  siret: 'Le numéro SIRET doit contenir 14 chiffres',
  codePostal: 'Le code postal doit contenir 5 chiffres',
  
  // Date validation
  invalidDate: 'Date invalide',
  dateFuture: 'La date ne peut pas être dans le futur',
  datePast: 'La date ne peut pas être dans le passé',
  
  // Email/Phone
  invalidEmail: 'Adresse email invalide',
  invalidPhone: 'Numéro de téléphone invalide',
  
  // Address
  addressRequired: 'L\'adresse est obligatoire',
  villeRequired: 'La ville est obligatoire',
  
  // Legal specific
  typeActeRequired: 'Le type d\'acte est obligatoire',
  formeJuridiqueRequired: 'La forme juridique est obligatoire',
  notaireRequired: 'Le nom du notaire est obligatoire pour un acte authentique',
  officiantRequired: 'Le lieu d\'exercice du notaire est obligatoire',
  objetSocialRequired: 'L\'objet social est obligatoire',
  objetSocialMinLength: 'L\'objet social doit contenir au moins 10 caractères',
};

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validates a single field value
 * @param {string} fieldName - Name of the field being validated
 * @param {any} value - Value to validate
 * @param {object} rules - Validation rules
 * @returns {string|null} - Error message or null if valid
 */
export function validateField(fieldName, value, rules = {}) {
  const {
    required = false,
    minLength,
    maxLength,
    min,
    max,
    pattern,
    customValidator,
    customMessage,
  } = rules;

  // Required check
  if (required) {
    if (value === undefined || value === null || value === '') {
      return getRequiredMessage(fieldName);
    }
    if (typeof value === 'string' && value.trim() === '') {
      return getRequiredMessage(fieldName);
    }
  }

  // Skip other validations if value is empty and not required
  if (!value && value !== 0) {
    return null;
  }

  // String length validations
  if (typeof value === 'string') {
    if (minLength && value.length < minLength) {
      return VALIDATION_MESSAGES.minLength(minLength);
    }
    if (maxLength && value.length > maxLength) {
      return VALIDATION_MESSAGES.maxLength(maxLength);
    }
  }

  // Number validations
  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
    const numValue = Number(value);
    if (min !== undefined && numValue < min) {
      return VALIDATION_MESSAGES.minValue(min);
    }
    if (max !== undefined && numValue > max) {
      return VALIDATION_MESSAGES.maxValue(max);
    }
  }

  // Pattern validation
  if (pattern && typeof value === 'string') {
    if (!pattern.test(value)) {
      return customMessage || 'Format invalide';
    }
  }

  // Custom validator
  if (customValidator && typeof customValidator === 'function') {
    const customError = customValidator(value);
    if (customError) {
      return customError;
    }
  }

  return null;
}

/**
 * Get appropriate required message based on field name
 */
function getRequiredMessage(fieldName) {
  const fieldMessages = {
    type_acte: VALIDATION_MESSAGES.typeActeRequired,
    denomination: VALIDATION_MESSAGES.denomination,
    forme_juridique: VALIDATION_MESSAGES.formeJuridiqueRequired,
    capital: VALIDATION_MESSAGES.capital,
    objet: VALIDATION_MESSAGES.objetSocialRequired,
    nom_famille: VALIDATION_MESSAGES.notaireRequired,
    officiant_to: VALIDATION_MESSAGES.officiantRequired,
    adresse: VALIDATION_MESSAGES.addressRequired,
    ville: VALIDATION_MESSAGES.villeRequired,
    code_postal: VALIDATION_MESSAGES.codePostal,
    date_acte: VALIDATION_MESSAGES.requiredDate,
  };

  return fieldMessages[fieldName] || VALIDATION_MESSAGES.required;
}

/**
 * Validates multiple fields at once
 * @param {object} formData - Object containing all form field values
 * @param {object} validationSchema - Object mapping field names to validation rules
 * @returns {object} - Object with field names as keys and error messages as values
 */
export function validateForm(formData, validationSchema) {
  const errors = {};

  Object.keys(validationSchema).forEach((fieldName) => {
    const rules = validationSchema[fieldName];
    const value = formData[fieldName];
    const error = validateField(fieldName, value, rules);
    
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
}

/**
 * Check if form has any errors
 * @param {object} errors - Errors object from validateForm
 * @returns {boolean}
 */
export function hasErrors(errors) {
  return Object.keys(errors).length > 0;
}

// ============================================
// PREDEFINED VALIDATION SCHEMAS FOR LEGAL FORMS
// ============================================

/**
 * Base validation schema for company creation forms (SARL, SAS, etc.)
 */
export const CREATION_FORM_SCHEMA = {
  type_acte: { required: true },
  denomination: { required: true, minLength: 2, maxLength: 100 },
  forme_juridique: { required: true },
  objet: { required: true, minLength: 10 },
  capital: { required: true, min: 1 },
  duree: { required: true, min: 1, max: 99 },
  adresse: { required: true },
  code_postal: { 
    required: true, 
    pattern: /^\d{5}$/,
    customMessage: VALIDATION_MESSAGES.codePostal 
  },
  ville: { required: true },
};

/**
 * Validation schema for notaire fields (when type_acte is ACTE_AUTHENTIQUE)
 */
export const NOTAIRE_SCHEMA = {
  nom_famille: { required: true, minLength: 2 },
  officiant_to: { required: true, minLength: 2 },
};

/**
 * Validation schema for gérant/associé fields
 */
export const GERANT_SCHEMA = {
  civilite_lagrance: { required: true },
  nomlagrance: { required: true, minLength: 2 },
  prenomlagrance: { required: true, minLength: 2 },
  adresselagrance: { required: true },
  code_postallagrance: { 
    required: true, 
    pattern: /^\d{5}$/,
    customMessage: VALIDATION_MESSAGES.codePostal 
  },
  villelagrance: { required: true },
};

// ============================================
// SPECIFIC FIELD VALIDATORS
// ============================================

/**
 * Validate SIREN number (9 digits)
 */
export function validateSiren(value) {
  if (!value) return null;
  const cleaned = value.replace(/\s/g, '');
  if (!/^\d{9}$/.test(cleaned)) {
    return VALIDATION_MESSAGES.siren;
  }
  return null;
}

/**
 * Validate SIRET number (14 digits)
 */
export function validateSiret(value) {
  if (!value) return null;
  const cleaned = value.replace(/\s/g, '');
  if (!/^\d{14}$/.test(cleaned)) {
    return VALIDATION_MESSAGES.siret;
  }
  return null;
}

/**
 * Validate French postal code (5 digits)
 */
export function validateCodePostal(value) {
  if (!value) return null;
  if (!/^\d{5}$/.test(value)) {
    return VALIDATION_MESSAGES.codePostal;
  }
  return null;
}

/**
 * Validate capital social (must be positive number)
 */
export function validateCapital(value) {
  if (!value && value !== 0) return VALIDATION_MESSAGES.capital;
  const num = Number(value);
  if (isNaN(num) || num <= 0) {
    return VALIDATION_MESSAGES.capital;
  }
  return null;
}

/**
 * Validate company duration (1-99 years)
 */
export function validateDuree(value) {
  if (!value && value !== 0) return VALIDATION_MESSAGES.duree;
  const num = Number(value);
  if (isNaN(num) || num < 1 || num > 99) {
    return VALIDATION_MESSAGES.duree;
  }
  return null;
}

/**
 * Validate email format
 */
export function validateEmail(value) {
  if (!value) return null;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return VALIDATION_MESSAGES.invalidEmail;
  }
  return null;
}

/**
 * Validate French phone number
 */
export function validatePhone(value) {
  if (!value) return null;
  const cleaned = value.replace(/[\s.-]/g, '');
  // French phone: starts with 0 and has 10 digits, or +33 with 11-12 digits
  if (!/^(0\d{9}|\+33\d{9,10})$/.test(cleaned)) {
    return VALIDATION_MESSAGES.invalidPhone;
  }
  return null;
}

// ============================================
// HELPER HOOK FOR FORM VALIDATION STATE
// ============================================

/**
 * Creates a validation state manager
 * Usage in component:
 * 
 * const [errors, setErrors] = useState({});
 * const { validateOnBlur, validateOnSubmit, clearError } = useValidation(setErrors);
 * 
 * <TextField
 *   onBlur={() => validateOnBlur('denomination', formData.denomination, { required: true })}
 *   error={!!errors.denomination}
 *   helperText={errors.denomination}
 * />
 */
export function createValidationHandlers(setErrors) {
  return {
    /**
     * Validate a single field on blur
     */
    validateOnBlur: (fieldName, value, rules) => {
      const error = validateField(fieldName, value, rules);
      setErrors((prev) => {
        if (error) {
          return { ...prev, [fieldName]: error };
        }
        // Remove error if field is now valid
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
      return error;
    },

    /**
     * Validate entire form on submit
     */
    validateOnSubmit: (formData, schema) => {
      const errors = validateForm(formData, schema);
      setErrors(errors);
      return !hasErrors(errors);
    },

    /**
     * Clear error for a specific field
     */
    clearError: (fieldName) => {
      setErrors((prev) => {
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
    },

    /**
     * Clear all errors
     */
    clearAllErrors: () => {
      setErrors({});
    },
  };
}

const formValidation = {
  validateField,
  validateForm,
  hasErrors,
  createValidationHandlers,
  VALIDATION_MESSAGES,
  CREATION_FORM_SCHEMA,
  NOTAIRE_SCHEMA,
  GERANT_SCHEMA,
  validateSiren,
  validateSiret,
  validateCodePostal,
  validateCapital,
  validateDuree,
  validateEmail,
  validatePhone,
};

export default formValidation;
